import React, { useState, useEffect, useRef } from "react";
import { API, Auth } from "aws-amplify";
import {
  Route,
  Link,
  useRouteMatch,
  Switch,
  Redirect,
  useParams
} from "react-router-dom";
import { cookieAttributes } from "util/resumeCookieSettings";
import classes from "./OpenQuery.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

import "../../assets/_master.scss";
import { getIcon } from "util/iconMapper";
import { buildQueryBreadcrumbs, reportHeading } from "features/queryReports/utils/commonFunctions";
import { Breadcrumbs } from "features/breadcrumb/breadcrumbs";
import { useAuthReady } from "features/queryReports/hooks/useAuthReady";

function OpenQueryContainer() {
  useEffect(() => {
    async function setResumeCookie() {
      const cookieValue = encodeURIComponent(`QUERY - /openquery/commercial`);
      const resumeCookie = Object.assign(cookieAttributes(), { name: 'platform_resume' }, { value: cookieValue });
      await cookieStore.set(resumeCookie);
    }
    setResumeCookie();
  }, []);

  const [dynamicReportList, setDynamicReportList] = useState([]);

  const reportsInFlight = useRef(false);

  const { path, url } = useRouteMatch();
  const { payerMix } = useParams();
  const items = buildQueryBreadcrumbs({ payerMix, reportHeading });

  // --- Auth readiness ---
  const ready = useAuthReady();

  // --- Fetch reports only when authenticated ---
  useEffect(() => {
    if (!ready) return;
    if (reportsInFlight.current) return;
    reportsInFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check
        const reports = await API.get("OpenQueryAPI", `/openquery/reports`);
        if (cancelled) return;

        // Include ".Combo" => "commercial-and-medicare"
        const shapedReports = reports.reports.category.reduce(
          (tabsArr, category) => {
            const categoryContents = category.reports.reduce(
              (reportSet, report) => {
                if (report.name.includes(".Medicare")) {
                  reportSet.add("medicare");
                } else if (report.name.includes(".Combo")) {
                  reportSet.add("commercial-and-medicare");
                } else {
                  reportSet.add("commercial");
                }
                return reportSet;
              },
              new Set()
            );
            categoryContents.forEach((cat) => {
              tabsArr.push({
                name: category.name,
                description: category.description,
                id: category.id,
                type: cat,
              });
            });
            return tabsArr;
          },
          []
        );
        const marketShare = [];
        const clinicalMarketShare = [];
        const others = [];

        shapedReports.forEach(c => {
          if (c.name === "Market Share") {
            marketShare.push(c);
          } else if (c.name === "Clinical Market Share") {
            clinicalMarketShare.push(c);
          } else {
            others.push(c);
          }
        });

        const ordered = [...marketShare, ...clinicalMarketShare, ...others];
        setDynamicReportList(ordered);
      } catch (err) {
        console.error("error fetching openquery reports", err);
      } finally {
        reportsInFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready]);

  return (
    <>
      {/* Allow the combined tab */}
      {["commercial", "medicare", "commercial-and-medicare"].indexOf(
        payerMix
      ) === -1 ? (
        <Redirect to="/openquery/commercial" />
      ) : null}
      <Route exact path="/openquery">
        <Redirect to="/openquery/commercial" />
      </Route>
      <div className={`query-wrapper ${classes["create-query-wrapper"]}`}>
        <Breadcrumbs items={items} />
        <div className={classes["head-wrapper"]}>
          <h3 className={classes["heading"]}>Create a New Query</h3>
          <span className={classes["subtitle"]}>
            Configure your data query parameters and preview results
          </span>
        </div>

        <div className={classes["content-wraper"]}>
          <div className={classes["card-header"]}>
            <ul
              className={`${classes.nav} nav nav-tabs ${classes["card-header-tabs"]}`}
            >
              <li className="nav-item">
                <div
                  className={
                    payerMix === "commercial"
                      ? `${classes["nav-link"]} ${classes["active"]}`
                      : classes["nav-link"]
                  }
                >
                  <Link to="/openquery/commercial">Commercial</Link>
                </div>
              </li>
              <li className="nav-item">
                <div
                  className={
                    payerMix === "medicare"
                      ? `${classes["nav-link"]} ${classes["active"]}`
                      : classes["nav-link"]
                  }
                >
                  <Link to="/openquery/medicare">Medicare</Link>
                </div>
              </li>
              {/* Third tab for combined */}
              <li className="nav-item">
                <div
                  className={
                    payerMix === "commercial-and-medicare"
                      ? `${classes["nav-link"]} ${classes["active"]}`
                      : classes["nav-link"]
                  }
                >
                  <Link to="/openquery/commercial-and-medicare">
                    Commercial & Medicare
                  </Link>
                </div>
              </li>
            </ul>
          </div>
          <div className={classes["report-content-wrap"]}>
            <div className={classes["head-title"]}>{reportHeading(payerMix)} Export Types</div>
            <ul className={classes["report-list-wrap"]}>
              {dynamicReportList
                .filter(item => payerMix === item.type)
                .map(item => (
                  <li key={item.name} className={classes["list-group-item"]}>
                    <div className={classes["left-content"]}>
                      <span
                        className={classes["icon"]}
                        style={{ backgroundColor: getIcon(item)?.color }}
                      >
                        <img
                          src={getIcon(item)?.icon}
                          alt={item?.name || "icon"}
                        />
                      </span>
                      <div>
                        <Switch>
                          <Route path={`${path}/${item.id}`}>
                            <Link to={`/openquery/${payerMix}`}>
                              <span className={classes["name"]}>
                                {item.name}
                              </span>
                            </Link>
                          </Route>

                          <Route path="/openquery">
                            <Link to={`${url}/${item.id}`}>
                              <span className={classes["name"]}>
                                {item.name}
                              </span>
                            </Link>
                          </Route>
                        </Switch>

                        <div className={classes["desc"]}>
                          {item.description}
                        </div>
                      </div>
                    </div>
                    <Switch>
                      <Route path={`${path}/${item.id}`}>
                        <Link to={`/openquery/${payerMix}`}>
                          <div className={classes["create-btn"]}>
                            Create Query <FontAwesomeIcon icon={faArrowRight} />
                          </div>
                        </Link>
                      </Route>

                      <Route path="/openquery">
                        <Link to={`${url}/${item.id}`}>
                          <div className={classes["create-btn"]}>
                            Create Query <FontAwesomeIcon icon={faArrowRight} />
                          </div>
                        </Link>
                      </Route>
                    </Switch>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

export default OpenQueryContainer;
